import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsCompactViewport } from '../../hooks/useIsCompactViewport';
import './InventoryBox.css';

/**
 * InventoryBox Component
 *
 * Displays collected items as small icons at bottom of room, compact design that
 * doesn't block gameplay. Key-type items render the real key image; every collected
 * item's tooltip shows its name and where it was found.
 *
 * collectedCodes is optional legacy support for rooms that still want a raw numeric-code
 * tray (music/chemistry rooms) - rooms that don't pass it (e.g. the algebra room) simply
 * don't show one, so codes must be remembered from the moment they were revealed.
 *
 * @param {Array} collectedItems - Array of item IDs that have been collected
 * @param {Object} roomConfig - Room configuration with puzzle item details
 * @param {boolean} showLabels - Whether to show item labels on hover
 * @param {Function} onItemClick - Optional callback when item is clicked
 * @param {Object} itemTypeById - Map of item_id -> 'key' | 'code', used to render the real key image
 * @param {Object} codesByItemId - Map of item_id -> numeric code string. Hovering (or tapping,
 *   on touch devices) a code-type item shows its digits, since the door itself never reveals them.
 * @param {Object} sourceByItemId - Map of item_id -> the puzzle/game name it was retrieved from
 *   (e.g. "Molecule Builder", "Rocket Launch Puzzle"). Shown in the same hover/tap tooltip as
 *   the item's name, since neither the door nor (for codes) the icon itself says where it came from.
 * @param {Function} onReset - Optional callback to reset all room progress
 * @param {Array} collectedCodes - Optional array of {code, source, icon} - legacy code tray
 * @param {Array} extraItems - Optional additional {item_id, item_name, item_icon, item_description}
 *   entries shown in the same tray (e.g. sequencing objects like a lens or rope), kept out of
 *   the "collected/total" counter above since that counter is specifically about puzzle progress
 * @param {Object} usedItemIds - Map of item_id -> true for sequencing objects already spent to
 *   unlock their target puzzle - single-use, so these render exhausted (dimmed, unclickable)
 *   instead of disappearing, so the player can still see what they've used.
 * @param {string} openItemId - item_id of a slot to highlight as "currently open/in use" -
 *   unlike usedItemIds, this never disables the slot (e.g. a tool panel toggled open/closed
 *   by clicking its own icon repeatedly, not a single-use consumable). Distinct from the
 *   component's own internal `activeItemId` (the hovered/tapped-for-tooltip item).
 * @param {Object} doneItemIds - Map of item_id -> true for a small always-visible checkmark
 *   badge (not just on hover/tap), for a reusable tool that's already been used at least once
 *   (e.g. a sextant reading already recorded) - unlike usedItemIds, this never disables the
 *   slot, since "done once" isn't "spent" for something reopenable.
 * @param {string} counterLabel - Optional override for the badge's own "collected/total" text.
 *   Some rooms cap how many items can be HELD at once at a number smaller than the room's full
 *   item count (e.g. a 3-book satchel out of an 87-book shelf) - the default "collected/total"
 *   reads as puzzle progress, not that cap, so a room like that can pass its own text (e.g.
 *   "3/3 - Trade & Commerce") instead of a second, separate badge fighting this one for the
 *   same corner of the screen.
 */
function InventoryBox({ collectedItems = [], roomConfig = {}, showLabels = true, onItemClick, itemTypeById = {}, codesByItemId = {}, sourceByItemId = {}, onReset, collectedCodes = [], extraItems = [], usedItemIds = {}, openItemId = null, doneItemIds = {}, counterLabel = null }) {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [revealedItemId, setRevealedItemId] = useState(null);
  // Anchor point (viewport coords) for whichever item's tooltip is currently showing -
  // captured from the icon's own bounding rect at the moment it's hovered/tapped. The
  // tooltip itself is portaled to document.body (see below) rather than rendered inline,
  // so this is how it knows where to draw itself.
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  // Collapsible on every viewport, not just short-landscape phones - defaults match the old
  // per-viewport behavior (open on desktop/portrait, collapsed on short-landscape) but the
  // badge is now always the toggle, so the tray can be tucked out of the way anywhere.
  const isCompactViewport = useIsCompactViewport();
  const [isExpanded, setIsExpanded] = useState(() => !isCompactViewport);
  const puzzles = roomConfig.puzzles || {};

  // Re-apply the viewport-appropriate default when the breakpoint itself changes (e.g. the
  // device is rotated) - doesn't fight a mid-session manual toggle otherwise, since this only
  // fires when isCompactViewport's value actually flips.
  useEffect(() => {
    setIsExpanded(!isCompactViewport);
  }, [isCompactViewport]);

  // Tap-to-reveal tooltips (touch devices, or a mouse click) only ever cleared themselves
  // when the *same* icon was clicked again - there was no way to dismiss one by tapping
  // anywhere else, so it looked "stuck" forever once opened. Clicking anywhere outside the
  // whole bar now clears it; clicks on an icon are left alone so the existing open/switch/
  // toggle-off logic in each slot's own onClick (below) still runs unmodified.
  useEffect(() => {
    if (!revealedItemId) return undefined;
    const handlePointerDown = (event) => {
      if (event.target.closest('.inventory-bottom-bar')) return;
      setRevealedItemId(null);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [revealedItemId]);

  // Get all possible items for this room
  const allItems = Object.values(puzzles);
  // Rendered alongside allItems, but not counted in the "X/Y" badge below.
  const displayedItems = [...allItems, ...extraItems];

  if (allItems.length === 0 && extraItems.length === 0) return null;

  const activeItemId = hoveredItem || revealedItemId;
  const activeItem = (activeItemId && collectedItems.includes(activeItemId))
    ? displayedItems.find((item) => item.item_id === activeItemId)
    : null;
  const activeSource = activeItemId ? sourceByItemId[activeItemId] : null;
  const activeIsUsed = activeItemId ? Boolean(usedItemIds[activeItemId]) : false;
  const activeIsCodeItem = activeItemId ? (itemTypeById[activeItemId] === 'code' && !!codesByItemId[activeItemId]) : false;

  const showContent = isExpanded;

  return (
    <div className="inventory-bottom-bar">
      <motion.div
        className="inventory-bottom-bar-inner"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        onMouseLeave={() => {
          // The tap-revealed tooltip (revealedItemId) doesn't track hover at all, so a
          // desktop user who clicks an icon then just moves the mouse away (without
          // clicking anything else) kept seeing it - hoveredItem cleared on that same
          // mouseleave, but activeItemId falls back to revealedItemId, which didn't. Show
          // it only while the pointer is actually within the tray, on any device: clearing
          // both here means leaving the bar always hides it, whether it was opened by hover
          // or by tap.
          setHoveredItem(null);
          setRevealedItemId(null);
        }}
      >
        {/* Compact counter badge - always doubles as the expand/collapse toggle now, on
            every viewport, not just short-landscape phones. */}
        <div
          className="inventory-counter-badge clickable"
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <span className="counter-icon">🎒</span>
          <span className="counter-text">{counterLabel || `${collectedItems.length}/${allItems.length}`}</span>
          <span className="counter-toggle-chevron">{isExpanded ? '▾' : '▸'}</span>
        </div>

        {showContent && (
        <>
        {/* Horizontal icon row for items - scrollable so a long list never
            overflows the (often narrow) bar footprint. */}
        <div className="inventory-icons-row">
          <AnimatePresence>
            {displayedItems.map((itemInfo, index) => {
              const isCollected = collectedItems.includes(itemInfo.item_id);
              const isHovered = hoveredItem === itemInfo.item_id;
              const isKey = itemTypeById[itemInfo.item_id] === 'key';
              const code = codesByItemId[itemInfo.item_id];
              const isCodeItem = itemTypeById[itemInfo.item_id] === 'code' && !!code;
              const isRevealed = revealedItemId === itemInfo.item_id;
              const isUsed = Boolean(usedItemIds[itemInfo.item_id]);
              const isOpen = openItemId != null && openItemId === itemInfo.item_id;
              const isDone = Boolean(doneItemIds[itemInfo.item_id]);

              const isTooltipActive = isCollected && !isUsed && (isHovered || isRevealed);

              return (
                <motion.div
                  key={itemInfo.item_id}
                  className={`inventory-icon-slot ${isCollected ? 'collected' : 'empty'} ${isUsed ? 'used' : ''} ${isTooltipActive ? 'tooltip-active' : ''} ${isOpen ? 'open' : ''}`}
                  aria-label={isCollected ? itemInfo.item_name : undefined}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ delay: index * 0.05, type: 'spring', stiffness: 200 }}
                  onMouseEnter={(event) => {
                    setHoveredItem(itemInfo.item_id);
                    const rect = event.currentTarget.getBoundingClientRect();
                    setTooltipAnchor({ left: rect.left + rect.width / 2, top: rect.top });
                  }}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={(event) => {
                    if (!isCollected || isUsed) return;
                    const rect = event.currentTarget.getBoundingClientRect();
                    setRevealedItemId((prev) => {
                      const next = prev === itemInfo.item_id ? null : itemInfo.item_id;
                      if (next) setTooltipAnchor({ left: rect.left + rect.width / 2, top: rect.top });
                      return next;
                    });
                    if (onItemClick) onItemClick(itemInfo);
                  }}
                  style={{ cursor: isCollected && !isUsed ? 'pointer' : 'default' }}
                  whileHover={isCollected && !isUsed ? { scale: 1.2, y: -8 } : {}}
                >
                  {isCollected ? (
                    <>
                      {isKey ? (
                        <motion.img
                          src="/assets/escape-rooms/common/key.png"
                          alt={itemInfo.item_name}
                          className="icon-key-image"
                          initial={{ rotate: -180, scale: 0 }}
                          animate={{ rotate: 0, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 150, damping: 10 }}
                        />
                      ) : isCodeItem ? (
                        // The code itself, always visible right on the icon - no hover/tap
                        // needed - since it's the one thing the player actually has to
                        // remember to use elsewhere (a key just is "the key").
                        <motion.span
                          className="icon-code-inline"
                          initial={{ rotate: -180, scale: 0 }}
                          animate={{ rotate: 0, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 150, damping: 10 }}
                        >
                          {code}
                        </motion.span>
                      ) : itemInfo.item_image ? (
                        // Real tool artwork (paint brush, battery, tinted glasses, etc.) -
                        // falls back to the emoji below for any tool that doesn't have one
                        // yet, so a missing asset never blanks out the slot.
                        <motion.img
                          src={itemInfo.item_image}
                          alt={itemInfo.item_name}
                          className="icon-tool-image"
                          initial={{ rotate: -180, scale: 0 }}
                          animate={{ rotate: 0, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 150, damping: 10 }}
                        />
                      ) : (
                        <motion.span
                          className="icon-symbol"
                          initial={{ rotate: -180, scale: 0 }}
                          animate={{ rotate: 0, scale: 1 }}
                          transition={{ type: 'spring', stiffness: 150, damping: 10 }}
                        >
                          {itemInfo.item_icon || '📦'}
                        </motion.span>
                      )}

                      {isUsed && <span className="icon-used-badge">✓</span>}
                      {!isUsed && isDone && <span className="icon-done-badge">✓</span>}
                    </>
                  ) : (
                    <span className="icon-placeholder">○</span>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Tooltip for the active (hovered, or tapped on touch) item - portaled straight to
            document.body instead of rendered inline inside the icon slot above. This row
            scrolls horizontally (overflow-x: auto), and per the CSS overflow spec, setting
            overflow-x to anything but 'visible' forces overflow-y to compute as 'auto' too -
            even when overflow-y is explicitly set to 'visible' here. That silently clipped
            the tooltip entirely, since it pops up well above the row's own height. Portaling
            it to the body sidesteps that ancestor clipping (and any stacking-context
            ordering against sibling icons) altogether - position is computed from the
            hovered/tapped icon's own bounding rect (see tooltipAnchor above). */}
        {showLabels && createPortal(
          <AnimatePresence>
            {activeItem && tooltipAnchor && (
              // Plain (non-motion) wrapper handles the actual fixed positioning - centering
              // over the anchor point and lifting clear of the icon - via a real CSS
              // transform. It has to live on a separate, non-Framer element: Framer Motion
              // always drives its own `transform` as an inline style (for the animate={{y}}
              // fade below), and inline styles beat any CSS class outright, so putting the
              // positioning transform on the motion.div itself got silently clobbered - the
              // tooltip lost its "lift above the icon" offset and hung down off the bottom
              // of the screen instead.
              <div
                key={activeItemId}
                className="icon-tooltip-portal-anchor"
                style={{ left: tooltipAnchor.left, top: tooltipAnchor.top }}
              >
                <motion.div
                  className={`icon-tooltip icon-tooltip-portal ${activeIsCodeItem ? 'icon-code-reveal' : ''}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="icon-tooltip-name">{activeItem.item_name}{activeIsUsed ? ' (used)' : ''}</div>
                  {activeSource && (
                    <div className="icon-tooltip-source">From: {activeSource}</div>
                  )}
                  {activeItem.item_description && (
                    <div className="icon-tooltip-desc">{activeItem.item_description}</div>
                  )}
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Legacy secret codes tray - only rendered for rooms that still pass collectedCodes */}
        {collectedCodes.length > 0 && (
          <div className="inventory-codes-section">
            <div className="codes-divider"></div>
            <AnimatePresence>
              {collectedCodes.map((codeData, index) => (
                <motion.div
                  key={`${codeData.code}-${index}`}
                  className="code-display"
                  initial={{ scale: 0, opacity: 0, rotate: -180 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  exit={{ scale: 0, opacity: 0, rotate: 180 }}
                  transition={{ delay: index * 0.1, type: 'spring', stiffness: 150 }}
                  onMouseEnter={() => setHoveredItem(`code-${index}`)}
                  onMouseLeave={() => setHoveredItem(null)}
                  whileHover={{ scale: 1.15, y: -8 }}
                >
                  <span className="code-icon">{codeData.icon}</span>
                  <span className="code-text">{codeData.code}</span>

                  <AnimatePresence>
                    {showLabels && hoveredItem === `code-${index}` && (
                      <motion.div
                        className="icon-tooltip code-tooltip"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {codeData.source}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Reset progress */}
        {onReset && (
          <button
            type="button"
            className="inventory-reset-button"
            title="Reset room progress"
            onClick={onReset}
          >
            ↺
          </button>
        )}
        </>
        )}
      </motion.div>
    </div>
  );
}

export default InventoryBox;
